/**
 * Cascarón de navegación.
 *
 * Replica la estructura exacta de la app desplegada, incluida la parte que se
 * pasa por alto con facilidad: hay DOS árboles de navegación distintos.
 *
 *   Escritorio (lg+)  →  sidebar fija de 240px con los 9 módulos.
 *   Móvil             →  barra inferior de 5 columnas; los otros 5 módulos
 *                        viven detrás de "More" (/more), que es una ruta real.
 *
 * Sin /more, cinco módulos serían inalcanzables desde el teléfono.
 */
import { Link, useRouterState } from '@tanstack/react-router'
import {
  BarChart3,
  Building2,
  CalendarClock,
  ClipboardCheck,
  LayoutGrid,
  LogOut,
  MoreHorizontal,
  Receipt,
  Search,
  Settings,
  Truck,
  Wrench,
} from 'lucide-react'
import type { ComponentType, ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { useSession } from '@/lib/session'
import { Button } from '@/components/ui/button'

export interface NavItem {
  label: string
  to: string
  icon: ComponentType<{ className?: string }>
  description: string
}

/** Los 9 del sidebar, en el orden exacto de la app original. */
export const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', to: '/dashboard', icon: LayoutGrid, description: 'Salud y costos de la flota' },
  { label: 'Fleet', to: '/fleet', icon: Truck, description: 'Vehículos y equipos' },
  { label: 'Inspections', to: '/inspections', icon: ClipboardCheck, description: 'Revisiones y hallazgos' },
  { label: 'Work Orders', to: '/work-orders', icon: Wrench, description: 'Reparaciones y servicios' },
  { label: 'Vendors', to: '/vendors', icon: Building2, description: 'Talleres, distribuidores y proveedores' },
  { label: 'Expenses', to: '/expenses', icon: Receipt, description: 'Combustible, peajes, seguros' },
  { label: 'Maintenance', to: '/maintenance', icon: CalendarClock, description: 'Programas preventivos' },
  { label: 'Reports', to: '/reports', icon: BarChart3, description: 'Costo por unidad' },
  { label: 'Settings', to: '/settings', icon: Settings, description: 'Perfil, roles y conexión' },
]

/** Los 4 primeros van a la barra inferior; el quinto slot es "More". */
export const MOBILE_PRIMARY = NAV_ITEMS.slice(0, 4)
export const MOBILE_SECONDARY = NAV_ITEMS.slice(4)

function Brand() {
  return (
    <div className="flex items-center gap-2.5">
      <span className="grid size-9 place-items-center rounded-xl bg-primary text-primary-foreground">
        <Truck className="size-5" aria-hidden />
      </span>
      <span className="leading-tight">
        <span className="block text-sm font-semibold text-foreground">FleetOps</span>
        <span className="block text-xs text-muted-foreground">Maintenance Suite</span>
      </span>
    </div>
  )
}

export function AppShell({ children }: { children: ReactNode }) {
  const { user, signOut } = useSession()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const isActive = (to: string) => pathname === to || pathname.startsWith(`${to}/`)

  return (
    <div className="min-h-screen bg-background">
      {/* ── Sidebar de escritorio ───────────────────────────────────────── */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col border-r border-sidebar-border bg-sidebar lg:flex no-print">
        <div className="px-4 py-4">
          <Brand />
        </div>

        <nav className="flex-1 space-y-1 px-3" aria-label="Navegación principal">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150',
                isActive(item.to)
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                  : 'text-sidebar-foreground hover:bg-muted',
              )}
              aria-current={isActive(item.to) ? 'page' : undefined}
            >
              <item.icon className="size-[18px] shrink-0" aria-hidden />
              {item.label}
            </Link>
          ))}
        </nav>

        {user && (
          <div className="border-t border-sidebar-border px-4 py-3">
            <p className="truncate text-sm font-medium text-foreground">{user.fullName}</p>
            <p className="text-xs text-muted-foreground capitalize">
              {user.roles.join(' · ') || 'sin rol'}
            </p>
            <button
              type="button"
              onClick={() => void signOut()}
              className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-md py-1 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              <LogOut className="size-4" aria-hidden />
              Sign out
            </button>
          </div>
        )}
      </aside>

      {/* ── Header pegajoso ─────────────────────────────────────────────── */}
      <header className="sticky top-0 z-20 flex h-14 items-center gap-2 border-b border-border bg-card/95 px-4 backdrop-blur lg:pl-64 no-print">
        <div className="flex items-center gap-2 lg:hidden">
          <Brand />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" type="button">
            <Search className="size-4" aria-hidden />
            <span className="hidden sm:inline">Search fleet</span>
            <span className="sr-only sm:hidden">Buscar en la flota</span>
          </Button>
        </div>
      </header>

      {/* pb-24 deja hueco para la barra inferior; en lg vuelve a pb-8 */}
      <main className="px-4 pt-4 pb-24 lg:pl-64 lg:pb-8">{children}</main>

      {/* ── Barra inferior móvil: 5 columnas fijas ──────────────────────── */}
      <nav
        className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-border bg-card pb-[env(safe-area-inset-bottom)] lg:hidden no-print"
        aria-label="Navegación móvil"
      >
        {MOBILE_PRIMARY.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className={cn(
              'flex min-h-[56px] flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors',
              isActive(item.to) ? 'text-primary' : 'text-muted-foreground',
            )}
            aria-current={isActive(item.to) ? 'page' : undefined}
          >
            <item.icon className="size-5" aria-hidden />
            {item.label}
          </Link>
        ))}
        <Link
          to="/more"
          className={cn(
            'flex min-h-[56px] flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors',
            MOBILE_SECONDARY.some((i) => isActive(i.to)) || isActive('/more')
              ? 'text-primary'
              : 'text-muted-foreground',
          )}
        >
          <MoreHorizontal className="size-5" aria-hidden />
          More
        </Link>
      </nav>
    </div>
  )
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string
  subtitle?: ReactNode
  actions?: ReactNode
}) {
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  )
}
